import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import { ApproveOrDeniedEPOViaEmail } from '../../services/expense-purchase-order';
import useQuery from '../../custom-hooks/useQuery';

/*
 * React 18 development StrictMode can mount -> unmount -> mount a component.
 * A useRef is recreated on the second mount, so it cannot reliably prevent
 * the approval API from being called twice.
 *
 * Keep one Promise per approval-link action at module scope. A StrictMode
 * remount reuses the same request instead of creating a second POST.
 * A full browser refresh reloads this module; the backend remains idempotent.
 */
const epoApprovalRequestCache = new Map();

const executeApprovalOnce = (key, requestFactory) => {
  if (!epoApprovalRequestCache.has(key)) {
    epoApprovalRequestCache.set(
      key,
      Promise.resolve().then(requestFactory)
    );
  }

  return epoApprovalRequestCache.get(key);
};

const getValue = (row, camelName, pascalName) => {
  if (!row || typeof row !== 'object') {
    return undefined;
  }

  return row[camelName] ?? row[pascalName];
};

const extractApprovalRows = (value) => {
  const rows = [];
  const visited = new Set();

  const walk = (current, depth = 0) => {
    if (
      current == null ||
      depth > 7 ||
      (typeof current !== 'object' && !Array.isArray(current))
    ) {
      return;
    }

    if (typeof current === 'object') {
      if (visited.has(current)) {
        return;
      }

      visited.add(current);
    }

    if (Array.isArray(current)) {
      current.forEach((item) => walk(item, depth + 1));
      return;
    }

    const hasApprovalShape =
      getValue(current, 'mailRowType', 'MailRowType') !== undefined ||
      getValue(current, 'actionResult', 'ActionResult') !== undefined ||
      getValue(current, 'isFinalApproved', 'IsFinalApproved') !== undefined;

    if (hasApprovalShape) {
      rows.push(current);
    }

    Object.values(current).forEach((item) => {
      if (item && typeof item === 'object') {
        walk(item, depth + 1);
      }
    });
  };

  walk(value);

  return rows;
};

const normalizeUpper = (value) =>
  String(value || '')
    .trim()
    .toUpperCase();

const isAlreadyProcessedMessage = (value) => {
  const text = String(value || '').toLowerCase();

  return (
    text.includes('already approved') ||
    text.includes('already denied') ||
    text.includes('already voted') ||
    text.includes('already processed') ||
    text.includes('already been fully approved')
  );
};

export const ThankyouPageForEpo = () => {
  const theme = useTheme();

  const { epoId } = useParams();
  const query = useQuery();

  const approvalCase = query.get('case') || '';
  const userId = query.get('userid') || '';
  const isApprove = query.get('isApprove') || '';

  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [recordedAction, setRecordedAction] = useState('');

  const normalizedAction = String(isApprove).toLowerCase();

  const isApproval = normalizedAction === 'true';
  const isDenial = normalizedAction === 'false';

  const isValidRequest =
    Boolean(epoId) &&
    Boolean(userId) &&
    (isApproval || isDenial);

  useEffect(() => {
    let isMounted = true;

    if (!isValidRequest) {
      setStatus('invalid');
      setMessage(
        'This EPO approval link is incomplete or invalid.'
      );

      return () => {
        isMounted = false;
      };
    }

    const requestKey = [
      'epo',
      epoId,
      userId,
      normalizedAction,
      approvalCase || 'needApproval',
    ].join('_');

    const submitApproval = async () => {
      try {
        setStatus('loading');
        setMessage('');

        const response =
          await executeApprovalOnce(
            requestKey,
            () =>
              ApproveOrDeniedEPOViaEmail(
                epoId,
                normalizedAction,
                userId,
                approvalCase
              )
          );

        if (!isMounted) {
          return;
        }

        if (response?.status === 200) {
          const rows =
            response?.data?.result ||
            response?.data?.Result ||
            response?.data?.data?.result ||
            response?.data?.data?.Result ||
            [];

          const responseRows =
            Array.isArray(rows) ? rows : [];

          const actorRow =
            responseRows.find(
              (row) =>
                row?.mailRowType === 'VoteRecorded' ||
                row?.MailRowType === 'VoteRecorded' ||
                row?.mailRowType === 'CurrentApprover' ||
                row?.MailRowType === 'CurrentApprover' ||
                row?.mailRowType === 'AlreadyProcessed' ||
                row?.MailRowType === 'AlreadyProcessed'
            ) ||
            responseRows[0] ||
            {};

          const mailRowType =
            actorRow?.mailRowType ||
            actorRow?.MailRowType ||
            '';

          const actionResult = String(
            actorRow?.actionResult ||
            actorRow?.ActionResult ||
            ''
          ).toUpperCase();

          if (mailRowType === 'VoteRecorded') {
            setStatus('vote-recorded');

            setMessage(
              actionResult === 'MPC_VOTE_DENIED'
                ? 'Your MPC denial vote has been recorded. The EPO is still waiting for the remaining MPC members.'
                : 'Your MPC approval vote has been recorded. The EPO is still waiting for the remaining MPC members.'
            );

            return;
          }
debugger
          if (mailRowType === 'AlreadyProcessed') {
            const wasDenied =
              actionResult === 'ALREADY_DENIED' ||
              actionResult.includes('DENIED');

            setStatus('already');

            setMessage(
              wasDenied
                ? 'This EPO has already been denied. Your action was already recorded and no new action was taken.'
                : 'This EPO has already been approved. Your action was already recorded and no new action was taken.'
            );

            return;
          }

          if (
            actionResult === 'MPC_MAJORITY_DENIED' ||
            actionResult === 'WORKFLOW_DENIED' ||
            actionResult === 'DENIED'
          ) {
            setStatus('denied');
            setMessage(
              actionResult === 'MPC_MAJORITY_DENIED'
                ? 'MPC voting is complete. The majority decision is Denied.'
                : 'Your decision has been recorded successfully.'
            );

            return;
          }

          if (
            actionResult === 'MPC_MAJORITY_APPROVED' ||
            actionResult === 'WORKFLOW_APPROVED' ||
            actionResult === 'APPROVED'
          ) {
            setStatus('approved');
            setMessage(
              actionResult === 'MPC_MAJORITY_APPROVED'
                ? 'MPC voting is complete. The majority decision is Approved.'
                : 'Your approval has been recorded successfully.'
            );

            return;
          }

          if (isApproval) {
            setStatus('approved');
            setMessage(
              'Your approval has been recorded successfully.'
            );
          } else {
            setStatus('denied');
            setMessage(
              'Your decision has been recorded successfully.'
            );
          }

          return;
        }

        setStatus('error');
        setMessage(
          'The EPO action could not be completed. Please contact the administrator.'
        );
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error('EPO approval request failed:', error);

        setStatus('error');
        setMessage(
          'The EPO action could not be completed. Please contact the administrator.'
        );
      }
    };

    submitApproval();

    return () => {
      isMounted = false;
    };
  }, [
    epoId,
    userId,
    normalizedAction,
    approvalCase,
    isApproval,
    isValidRequest,
  ]);

  const statusConfig = useMemo(() => {
    switch (status) {
      case 'approved':
        return {
          title: 'EPO Approved',
          subtitle:
            'The approval workflow was completed successfully.',
          icon: '✓',
          palette: theme.palette.success,
          statusLabel: 'Approved',
        };

      case 'denied':
        return {
          title: 'EPO Denied',
          subtitle:
            'The approval workflow was completed with a denial.',
          icon: '×',
          palette: theme.palette.error,
          statusLabel: 'Denied',
        };

      case 'vote-recorded':
        return {
          title: 'MPC Vote Recorded',
          subtitle:
            'Your vote was saved successfully. The final result will be decided after all active MPC members vote.',
          icon: '✓',
          palette: theme.palette.info,
          statusLabel: 'Vote Recorded',
        };

      case 'forwarded':
        return {
          title: 'Approval Recorded',
          subtitle:
            'This approval stage is complete and the EPO has moved forward.',
          icon: '✓',
          palette: theme.palette.success,
          statusLabel: 'Moved to Next Stage',
        };

      case 'already':
        return {
          title: 'Action Already Recorded',
          subtitle:
            'This link has already been processed.',
          icon: '✓',
          palette: theme.palette.info,
          statusLabel: 'Already Processed',
        };

      case 'invalid':
        return {
          title: 'Invalid Approval Link',
          subtitle:
            'The required approval information could not be verified.',
          icon: '!',
          palette: theme.palette.warning,
          statusLabel: 'Invalid',
        };

      case 'error':
        return {
          title: 'Unable to Verify EPO',
          subtitle:
            'The request could not be verified.',
          icon: '!',
          palette: theme.palette.error,
          statusLabel: 'Not Processed',
        };

      default:
        return {
          title: isApproval
            ? 'Processing EPO Approval'
            : isDenial
              ? 'Processing EPO Denial'
              : 'Processing EPO Request',
          subtitle:
            'Please wait while your request is being verified and processed.',
          icon: null,
          palette: theme.palette.primary,
          statusLabel: 'Processing',
        };
    }
  }, [
    status,
    isApproval,
    isDenial,
    theme.palette,
  ]);

  const isLoading = status === 'loading';

  const actionLabel =
    recordedAction ||
    (
      isApproval
        ? 'Approve'
        : isDenial
          ? 'Deny'
          : 'Unknown'
    );

  const actionPalette =
    status === 'vote-recorded' ||
      status === 'already'
      ? theme.palette.info
      : status === 'denied'
        ? theme.palette.error
        : isDenial && status !== 'approved'
          ? theme.palette.error
          : theme.palette.success;

  const alertSeverity =
    status === 'approved' ||
      status === 'forwarded'
      ? 'success'
      : status === 'denied'
        ? 'error'
        : status === 'vote-recorded' ||
          status === 'already'
          ? 'info'
          : status === 'invalid'
            ? 'warning'
            : 'error';

  const bottomText =
    isLoading
      ? 'Please keep this page open while the EPO action is being processed.'
      : status === 'vote-recorded'
        ? 'Your vote is complete. Other MPC members can continue voting from their own email links.'
        : status === 'forwarded'
          ? 'Your action is complete. The next approver will continue the workflow.'
          : status === 'already'
            ? 'No duplicate approval, denial, mail, or history action was created.'
            : status === 'approved' ||
              status === 'denied'
              ? 'Your response has been recorded. You may safely close this page.'
              : 'Please verify that you opened the latest EPO approval link from your email.';

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: {
          xs: 'calc(100vh - 120px)',
          md: 'calc(100vh - 90px)',
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        px: {
          xs: 2,
          sm: 3,
          md: 4,
        },
        py: {
          xs: 3,
          md: 5,
        },
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: 420,
          height: 420,
          borderRadius: '50%',
          bgcolor: alpha(
            theme.palette.primary.main,
            0.035
          ),
          top: -190,
          right: -130,
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          width: 320,
          height: 320,
          borderRadius: '50%',
          bgcolor: alpha(
            theme.palette.primary.main,
            0.025
          ),
          bottom: -170,
          left: -100,
          pointerEvents: 'none',
        }}
      />

      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 680,
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden',
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 18px 55px ${alpha(
            theme.palette.common.black,
            theme.palette.mode === 'dark'
              ? 0.28
              : 0.08
          )}`,
        }}
      >
        <Box
          sx={{
            height: 4,
            bgcolor: statusConfig.palette.main,
          }}
        />

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            px: {
              xs: 2.5,
              sm: 3.5,
            },
            py: 2.5,
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              flexShrink: 0,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(
                theme.palette.primary.main,
                0.1
              ),
              border: `1px solid ${alpha(
                theme.palette.primary.main,
                0.2
              )}`,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 15,
                color: 'primary.main',
                letterSpacing: 0.5,
              }}
            >
              EPO
            </Typography>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: {
                  xs: 14,
                  sm: 16,
                },
                color: 'text.primary',
              }}
            >
              Expense Purchase Order
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.25,
                color: 'text.secondary',
                fontSize: 12.5,
              }}
            >
              Approval Management
            </Typography>
          </Box>
        </Stack>

        <Divider />

        <Box
          sx={{
            px: {
              xs: 2.5,
              sm: 4,
            },
            py: {
              xs: 4,
              sm: 5,
            },
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              minHeight: 82,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  width: 78,
                  height: 78,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(
                    theme.palette.primary.main,
                    0.06
                  ),
                }}
              >
                <CircularProgress
                  size={42}
                  thickness={4}
                  color="primary"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 78,
                  height: 78,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(
                    statusConfig.palette.main,
                    0.08
                  ),
                  border: `2px solid ${alpha(
                    statusConfig.palette.main,
                    0.28
                  )}`,
                  color: statusConfig.palette.main,
                  fontWeight: 800,
                  fontSize: 40,
                  lineHeight: 1,
                }}
              >
                {statusConfig.icon}
              </Box>
            )}
          </Box>

          <Typography
            component="h1"
            sx={{
              color: isLoading
                ? 'text.primary'
                : statusConfig.palette.main,
              fontWeight: 700,
              fontSize: {
                xs: 23,
                sm: 28,
              },
              lineHeight: 1.25,
            }}
          >
            {statusConfig.title}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              mx: 'auto',
              maxWidth: 520,
              color: 'text.secondary',
              fontSize: {
                xs: 13.5,
                sm: 14.5,
              },
              lineHeight: 1.7,
            }}
          >
            {statusConfig.subtitle}
          </Typography>

          {!isLoading && message && (
            <Alert
              severity={alertSeverity}
              icon={false}
              sx={{
                mt: 3,
                borderRadius: 2,
                textAlign: 'left',
                border: `1px solid ${alpha(
                  statusConfig.palette.main,
                  0.2
                )}`,
                bgcolor: alpha(
                  statusConfig.palette.main,
                  0.05
                ),
                color: 'text.primary',
              }}
            >
              {message}
            </Alert>
          )}

          <Box
            sx={{
              mt: 3.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              bgcolor: alpha(
                theme.palette.background.default,
                theme.palette.mode === 'dark'
                  ? 0.25
                  : 0.45
              ),
              textAlign: 'left',
            }}
          >
            <DetailRow
              theme={theme}
              label="EPO Number"
              value={epoId ? `#${epoId}` : '-'}
            />

            <Divider />

            <DetailRow
              theme={theme}
              label="Requested Action"
              customValue={
                <Chip
                  size="small"
                  label={actionLabel}
                  sx={{
                    minWidth: 78,
                    fontWeight: 700,
                    bgcolor: alpha(
                      actionPalette.main,
                      0.08
                    ),
                    color: actionPalette.main,
                    border: `1px solid ${alpha(
                      actionPalette.main,
                      0.15
                    )}`,
                  }}
                />
              }
            />

            <Divider />

            <DetailRow
              theme={theme}
              label="Status"
              customValue={
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  {isLoading && (
                    <CircularProgress
                      size={15}
                      thickness={5}
                    />
                  )}

                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: isLoading
                        ? 'text.primary'
                        : statusConfig.palette.main,
                    }}
                  >
                    {statusConfig.statusLabel}
                  </Typography>
                </Stack>
              }
            />
          </Box>

          <Box
            sx={{
              mt: 3,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: alpha(
                theme.palette.primary.main,
                0.035
              ),
            }}
          >
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 12.5,
                lineHeight: 1.6,
              }}
            >
              {bottomText}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            px: 3,
            py: 1.75,
            bgcolor: alpha(
              theme.palette.background.default,
              0.5
            ),
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: 11.5,
              color: 'text.secondary',
            }}
          >
            SSWhite Unified ERP • EPO Approval
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const DetailRow = ({
  theme,
  label,
  value,
  customValue,
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{
        px: {
          xs: 2,
          sm: 2.5,
        },
        py: 1.7,
        '&:hover': {
          bgcolor: alpha(
            theme.palette.primary.main,
            0.025
          ),
        },
      }}
    >
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 13.5,
        }}
      >
        {label}
      </Typography>

      {customValue || (
        <Typography
          sx={{
            color: 'text.primary',
            fontSize: 13.5,
            fontWeight: 700,
          }}
        >
          {value}
        </Typography>
      )}
    </Stack>
  );
};
